import { useState, useMemo } from "react";
import { cn, getFileUrl } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { 
  Plus, Pencil, Trash2, FileText, RotateCw, 
  Download, Eye, BookOpen, LayoutGrid, List,
  Loader2, CheckCircle2, ChevronRight, FileType
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { DataTable, Column } from "@/components/tables/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useListState } from "@/hooks/useListState";
import { 
  useGetNotesQuery, 
  useCreateNoteMutation, 
  useUpdateNoteMutation, 
  useDeleteNoteMutation,
  Note 
} from "@/redux/slices/noteApi";
import { useGetChaptersQuery } from "@/redux/slices/chapterApi";
import { useGetSubjectsQuery } from "@/redux/slices/subjectApi";
import { useGetAcademicYearsQuery } from "@/redux/slices/academicYearApi";
import { useUploadFileMutation } from "@/redux/slices/uploadApi";
import { format } from "date-fns";

const schema = z.object({
  title: z.string().trim().min(2, "Title required"),
  description: z.string().max(500).optional().default(""),
  demoUrl: z.string().min(1, "Demo PDF required"),
  fullUrl: z.string().min(1, "Full PDF required"),
  thumbnail: z.string().optional().default(""),
  academicYearId: z.string().min(1, "Year required"),
  subjectId: z.string().min(1, "Subject required"),
  chapterId: z.string().min(1, "Chapter required"),
  isActive: z.boolean().default(true),
});
type FormValues = z.infer<typeof schema>;

export default function NotesPage() {
  const list = useListState();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  
  const { data, isFetching, refetch } = useGetNotesQuery(list.queryParams());
  const { data: yearsData } = useGetAcademicYearsQuery({ limit: 100 });
  const { data: subjectsData } = useGetSubjectsQuery({ limit: 1000 });
  const { data: chaptersData } = useGetChaptersQuery({ limit: 1000 });
  
  const [createNote, createM] = useCreateNoteMutation();
  const [updateNote, updateM] = useUpdateNoteMutation();
  const [deleteNote, deleteM] = useDeleteNoteMutation();
  const [uploadFile, uploadM] = useUploadFileMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Note | null>(null);
  const [toDelete, setToDelete] = useState<Note | null>(null);
  const [uploadingField, setUploadingField] = useState<"demoUrl" | "fullUrl" | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { 
      title: "", description: "", demoUrl: "", fullUrl: "", 
      thumbnail: "", academicYearId: "", subjectId: "", 
      chapterId: "", isActive: true 
    },
  });

  const watchYear = form.watch("academicYearId");
  const watchSubject = form.watch("subjectId");

  const filteredSubjects = useMemo(() => {
    if (!subjectsData?.data || !watchYear) return [];
    return subjectsData.data.filter(s => s.academicYearId === watchYear);
  }, [subjectsData, watchYear]);

  const filteredChapters = useMemo(() => {
    if (!chaptersData?.data || !watchSubject) return [];
    return chaptersData.data.filter(c => c.subjectId === watchSubject);
  }, [chaptersData, watchSubject]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: "demoUrl" | "fullUrl") => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingField(field);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await uploadFile({ formData, folder: "notes", subfolder: "documents" }).unwrap();
      form.setValue(field, res.path);
      toast.success(`${field === 'demoUrl' ? 'Demo' : 'Full'} PDF uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingField(null);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.reset({ 
      title: "", description: "", demoUrl: "", fullUrl: "", 
      thumbnail: "", academicYearId: "", subjectId: "", 
      chapterId: "", isActive: true 
    });
    setOpen(true);
  };

  const openEdit = (n: Note) => {
    setEditing(n);
    form.reset(n as any);
    setOpen(true);
  };

  const onSubmit = async (v: FormValues) => {
    try {
      if (editing) {
        await updateNote({ id: editing.id, body: v }).unwrap();
        toast.success("Note updated");
      } else {
        await createNote(v).unwrap();
        toast.success("Note created");
      }
      setOpen(false);
    } catch {
      toast.error("Failed to save note");
    }
  };

  const columns: Column<Note>[] = [
    { 
      key: "title", 
      header: "Note Title", 
      render: (r) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium">{r.title}</p>
            <p className="text-[10px] text-muted-foreground uppercase">{r.chapter?.name}</p>
          </div>
        </div>
      ) 
    },
    { key: "subject", header: "Subject", render: (r) => <span className="text-muted-foreground">{r.subject?.name}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.isActive ? "active" : "inactive"} /> },
    { key: "createdAt", header: "Created", render: (r) => format(new Date(r.createdAt), "dd MMM yyyy") },
  ];

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Study Notes (PDF)"
        description="Manage study materials and chapter notes"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button onClick={openCreate} className="shadow-lg shadow-primary/20">
              <Plus className="mr-2 h-4 w-4" /> Add New Note
            </Button>
          </div>
        }
      />

      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        {/* Unified Filter Bar */}
        <div className="flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
          <SearchBar 
            placeholder="Search notes by title..." 
            className="flex-1" 
            value={list.search}
            onChange={list.setSearch}
          />
          
          <div className="flex flex-wrap items-center gap-3">
            <Select value={list.filters.academicYearId || "all"} onValueChange={(v) => list.setFilter("academicYearId", v === "all" ? undefined : v)}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {yearsData?.data?.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={list.filters.subjectId || "all"} onValueChange={(v) => list.setFilter("subjectId", v === "all" ? undefined : v)}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Subjects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {subjectsData?.data?.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>

             <div className="flex rounded-lg border bg-muted/50 p-1">
                <Button variant={viewMode === 'grid' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode('grid')} className="h-8 w-8 p-0">
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className="h-8 w-8 p-0">
                  <List className="h-4 w-4" />
                </Button>
             </div>
          </div>
        </div>

        {/* Content Section */}
        {viewMode === 'grid' ? (
          <>
            {isFetching ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                  <div key={i} className="h-72 animate-pulse rounded-2xl bg-muted/50" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {data?.data?.map((note) => (
                  <Card key={note.id} className="group relative overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-1 border-none bg-card/60 backdrop-blur-sm shadow-sm ring-1 ring-border">
                    <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5">
                      <div className="absolute inset-0 flex items-center justify-center text-primary/10 transition-transform group-hover:scale-110">
                        <FileText className="h-24 w-24" />
                      </div>
                      <div className="absolute left-3 top-3 flex gap-2">
                        <Badge className="bg-primary/90 backdrop-blur-md shadow-sm">
                          {note.chapter?.name}
                        </Badge>
                        {!note.isActive && (
                          <Badge variant="secondary" className="bg-orange-100 text-orange-700">Inactive</Badge>
                        )}
                      </div>
                      
                      <div className="absolute bottom-3 right-3 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <Button size="icon" variant="secondary" className="h-8 w-8 rounded-full bg-white/90 shadow-md" onClick={() => openEdit(note)}>
                          <Pencil className="h-4 w-4 text-primary" />
                        </Button>
                        <Button size="icon" variant="secondary" className="h-8 w-8 rounded-full bg-white/90 shadow-md text-destructive" onClick={() => setToDelete(note)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <CardHeader className="p-4 pb-2">
                      <CardTitle className="line-clamp-1 text-base group-hover:text-primary transition-colors cursor-pointer" onClick={() => navigate(`/notes/${note.id}`)}>
                        {note.title}
                      </CardTitle>
                      <CardDescription className="line-clamp-2 text-xs h-8 leading-relaxed">
                        {note.description || "Interactive PDF study material for this chapter."}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="p-4 pt-0">
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground font-medium">
                        <BookOpen className="h-3.5 w-3.5 text-primary/60" />
                        <span>{note.subject?.name}</span>
                        <span className="text-border">•</span>
                        <span>{format(new Date(note.createdAt), "MMM d")}</span>
                      </div>
                    </CardContent>
                    
                    <CardFooter className="flex items-center gap-2 border-t p-3 bg-muted/10 group-hover:bg-muted/20 transition-colors">
                      <Button size="sm" variant="ghost" className="flex-1 text-xs gap-1.5 hover:bg-primary hover:text-white" onClick={() => navigate(`/notes/${note.id}`)}>
                        <Eye className="h-3.5 w-3.5" /> View
                      </Button>
                      <Button size="sm" variant="ghost" className="flex-1 text-xs gap-1.5" asChild>
                        <a href={getFileUrl(note.fullUrl)} download target="_blank">
                          <Download className="h-3.5 w-3.5" /> Get PDF
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
            
            {/* Grid View Pagination (Custom integrated style) */}
            {!isFetching && data && data.total > 0 && (
              <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t pt-6 sm:flex-row">
                 <p className="text-sm text-muted-foreground">Showing <span className="font-bold text-foreground">{data.data.length}</span> of {data.total} notes</p>
                 <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={list.page === 1} onClick={() => list.setPage(list.page - 1)}>Prev</Button>
                    <div className="flex gap-1">
                      {Array.from({ length: Math.ceil(data.total / list.limit) }).map((_, i) => (
                        <Button 
                          key={i} 
                          variant={list.page === i + 1 ? "default" : "ghost"} 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          onClick={() => list.setPage(i + 1)}
                        >
                          {i + 1}
                        </Button>
                      ))}
                    </div>
                    <Button variant="outline" size="sm" disabled={list.page >= Math.ceil(data.total / list.limit)} onClick={() => list.setPage(list.page + 1)}>Next</Button>
                 </div>
              </div>
            )}
          </>
        ) : (
          <DataTable
            columns={columns}
            rows={data?.data ?? []}
            loading={isFetching}
            total={data?.total ?? 0}
            page={list.page}
            limit={list.limit}
            onPageChange={list.setPage}
            onLimitChange={list.setLimit}
            actions={(row) => (
              <div className="flex justify-end gap-1">
                <Button size="icon" variant="ghost" onClick={() => navigate(`/notes/${row.id}`)}>
                  <Eye className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => openEdit(row)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setToDelete(row)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          />
        )}

        {/* Empty State */}
        {!isFetching && data?.total === 0 && (
          <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed py-20 bg-card/50">
            <div className="rounded-2xl bg-muted p-6 text-muted-foreground/40 shadow-inner">
              <FileText className="h-12 w-12" />
            </div>
            <h3 className="mt-6 text-xl font-bold tracking-tight text-foreground">No study notes found</h3>
            <p className="mt-2 max-w-xs text-center text-sm text-muted-foreground">We couldn't find any notes matching your criteria. Try adjusting your filters or search terms.</p>
            <Button onClick={openCreate} className="mt-8 shadow-lg shadow-primary/20">
              <Plus className="mr-2 h-4 w-4" /> Create Your First Note
            </Button>
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[95vh] max-w-2xl flex-col p-0 overflow-hidden shadow-2xl border-none">
          <DialogHeader className="p-8 pb-4 bg-primary/5">
            <DialogTitle className="text-2xl font-bold">
              {editing ? "Edit Note Details" : "Publish New Note"}
            </DialogTitle>
            <DialogDescription className="text-sm font-medium text-muted-foreground">
              Configure study materials with chapter access and document files.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-8 py-4">
            <form id="note-form" onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="title" className="text-sm font-semibold">Note Title</Label>
                <Input id="title" className="h-11 shadow-sm focus:ring-2 focus:ring-primary/20 transition-all" placeholder="e.g. Clinical Examination of Pulse" {...form.register("title")} />
                {form.formState.errors.title && <p className="text-xs font-medium text-destructive">{form.formState.errors.title.message}</p>}
              </div>

              {/* PDF Upload Area */}
              <div className="space-y-4 rounded-2xl border border-primary/10 bg-primary/[0.02] p-5 sm:col-span-2 shadow-inner">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold flex items-center gap-2 text-primary uppercase tracking-wider">
                    <FileType className="h-4 w-4" /> Document Management
                  </h4>
                  <Badge variant="outline" className="bg-white/80 font-medium text-[10px]">PDF FORMAT ONLY</Badge>
                </div>
                
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-muted-foreground uppercase tracking-tight">Free Preview (Demo)</Label>
                    <div className="relative group">
                      <Input type="file" accept=".pdf" className="hidden" id="demo-upload" onChange={(e) => handleFileUpload(e, "demoUrl")} />
                      <div 
                        className={cn(
                          "flex h-28 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed transition-all duration-300",
                          form.watch("demoUrl") 
                            ? "border-green-400 bg-green-50/30 hover:bg-green-50/50" 
                            : "border-muted-foreground/20 bg-muted/30 hover:border-primary/40 hover:bg-primary/[0.03]"
                        )}
                        onClick={() => document.getElementById("demo-upload")?.click()}
                      >
                        {uploadingField === 'demoUrl' ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            <span className="text-[10px] font-bold text-primary animate-pulse">UPLOADING...</span>
                          </div>
                        ) : form.watch("demoUrl") ? (
                          <>
                            <div className="rounded-full bg-green-500/10 p-2 text-green-600">
                              <CheckCircle2 className="h-6 w-6" />
                            </div>
                            <span className="text-[10px] font-bold text-green-700 truncate max-w-[150px] px-2 uppercase">File Secured</span>
                          </>
                        ) : (
                          <>
                            <div className="rounded-full bg-muted/50 p-2 text-muted-foreground group-hover:text-primary transition-colors">
                              <Download className="h-5 w-5" />
                            </div>
                            <span className="text-[10px] font-bold text-muted-foreground uppercase">Upload Demo</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-muted-foreground uppercase tracking-tight">Full Content (Locked)</Label>
                    <div className="relative group">
                      <Input type="file" accept=".pdf" className="hidden" id="full-upload" onChange={(e) => handleFileUpload(e, "fullUrl")} />
                      <div 
                        className={cn(
                          "flex h-28 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed transition-all duration-300",
                          form.watch("fullUrl") 
                            ? "border-primary bg-primary/5 hover:bg-primary/10" 
                            : "border-muted-foreground/20 bg-muted/30 hover:border-primary/40 hover:bg-primary/[0.03]"
                        )}
                        onClick={() => document.getElementById("full-upload")?.click()}
                      >
                        {uploadingField === 'fullUrl' ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            <span className="text-[10px] font-bold text-primary animate-pulse">UPLOADING...</span>
                          </div>
                        ) : form.watch("fullUrl") ? (
                          <>
                            <div className="rounded-full bg-primary/10 p-2 text-primary">
                              <CheckCircle2 className="h-6 w-6" />
                            </div>
                            <span className="text-[10px] font-bold text-primary truncate max-w-[150px] px-2 uppercase">File Locked</span>
                          </>
                        ) : (
                          <>
                            <div className="rounded-full bg-muted/50 p-2 text-muted-foreground group-hover:text-primary transition-colors">
                              <FileText className="h-5 w-5" />
                            </div>
                            <span className="text-[10px] font-bold text-muted-foreground uppercase">Upload Full PDF</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Academic Year</Label>
                <Select value={form.watch("academicYearId") || undefined} onValueChange={(v) => { form.setValue("academicYearId", v); form.setValue("subjectId", ""); form.setValue("chapterId", ""); }}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Year" /></SelectTrigger>
                  <SelectContent>{yearsData?.data?.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Subject</Label>
                <Select value={form.watch("subjectId") || undefined} onValueChange={(v) => { form.setValue("subjectId", v); form.setValue("chapterId", ""); }} disabled={!watchYear}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Subject" /></SelectTrigger>
                  <SelectContent>{filteredSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Chapter</Label>
                <Select value={form.watch("chapterId") || undefined} onValueChange={(v) => form.setValue("chapterId", v)} disabled={!watchSubject}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue placeholder="Select Chapter" /></SelectTrigger>
                  <SelectContent>{filteredChapters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Status</Label>
                <Select value={form.watch("isActive") ? "true" : "false"} onValueChange={(v) => form.setValue("isActive", v === "true")}>
                  <SelectTrigger className="h-11 shadow-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active (Students can see)</SelectItem>
                    <SelectItem value="false">Inactive (Hidden)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="description" className="text-sm font-semibold">Short Description</Label>
                <Textarea id="description" rows={3} className="resize-none shadow-sm focus:ring-2 focus:ring-primary/20" placeholder="Summary of what this study material covers..." {...form.register("description")} />
              </div>
            </form>
          </div>

          <DialogFooter className="border-t p-8 bg-muted/10 flex items-center justify-between sm:justify-end gap-3">
            <Button variant="ghost" onClick={() => setOpen(false)} className="font-bold uppercase tracking-tight text-xs h-11 px-6 hover:bg-background">Discard</Button>
            <Button 
              form="note-form" 
              type="submit" 
              disabled={createM.isLoading || updateM.isLoading || !!uploadingField}
              className="h-11 px-8 rounded-xl font-bold uppercase tracking-wider text-xs shadow-xl shadow-primary/20 group"
            >
              {createM.isLoading || updateM.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editing ? "Update Material" : "Publish Material"}
              <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => { if (toDelete) { await deleteNote(toDelete.id).unwrap(); toast.success("Document removed successfully"); setToDelete(null); } }}
        loading={deleteM.isLoading}
        title="Permanently remove document?"
        description={`"${toDelete?.title}" will be deleted from the system. This action cannot be undone.`}
      />
    </div>
  );
}
